import { doc, setDoc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase/config';
import type { UserProfile } from '../types/user';

export const createUserProfile = async (uid: string, userData: Partial<UserProfile>) => {
  try {
    console.log('Creating user profile for:', uid);
    const userRef = doc(db, 'users', uid);
    
    // Check if profile already exists
    const existingProfile = await getDoc(userRef);
    if (existingProfile.exists()) {
      console.log('User profile already exists, updating...');
      const updatedProfile = {
        ...existingProfile.data(),
        ...userData,
        updatedAt: new Date().toISOString()
      };
      await setDoc(userRef, updatedProfile, { merge: true });
      return updatedProfile as UserProfile;
    }
    
    console.log('Creating new user profile with data:', userData);
    const userProfile: UserProfile = {
      uid,
      email: userData.email || '',
      fullName: userData.fullName || userData.displayName || 'User',
      displayName: userData.displayName || 'User',
      phoneNumber: userData.phoneNumber || '',
      isEmailVerified: userData.isEmailVerified || false,
      profileImage: userData.profileImage || '',
      createdAt: userData.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      role: userData.role || 'user',
      addresses: userData.addresses || [],
    };

    await setDoc(userRef, userProfile);
    console.log('User profile created successfully');
    return userProfile;
  } catch (error) {
    console.error('Error creating user profile:', error);
    throw error;
  }
};

export const getUserProfile = async (uid: string) => {
  try {
    const userRef = doc(db, 'users', uid);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      return userSnap.data() as UserProfile;
    }
    
    return null;
  } catch (error) {
    console.error('Error fetching user profile:', error);
    throw error;
  }
};

export const updateUserProfile = async (uid: string, updates: Partial<UserProfile>) => {
  try {
    const userRef = doc(db, 'users', uid);
    await updateDoc(userRef, { ...updates, updatedAt: new Date().toISOString() });
  } catch (error) {
    console.error('Error updating user profile:', error);
    throw error;
  }
};

export const uploadProfileImage = async (uid: string, file: File) => {
  try {
    const storageRef = ref(storage, `users/${uid}/profile.jpg`);
    await uploadBytes(storageRef, file);
    const downloadURL = await getDownloadURL(storageRef);
    await updateUserProfile(uid, { profileImage: downloadURL });
    return downloadURL;
  } catch (error) {
    console.error('Error uploading profile image:', error);
    throw error;
  }
};

export const calculateAndUpdateAverageRating = async (userId: string) => {
  try {
    // Collect ratings from both 'reviews' (generic) and 'customerFeedbacks' (customer -> provider)
    const reviewsRef = collection(db, 'reviews');
    const reviewsQuery = query(reviewsRef, where('userId', '==', userId));
    const reviewsSnapshot = await getDocs(reviewsQuery).catch(() => null);

    const feedbackRef = collection(db, 'customerFeedbacks');
    const feedbackQuery = query(feedbackRef, where('providerId', '==', userId));
    const feedbackSnapshot = await getDocs(feedbackQuery).catch(() => null);

    let totalRating = 0;
    let count = 0;

    if (reviewsSnapshot && !reviewsSnapshot.empty) {
      reviewsSnapshot.forEach((d) => {
        const r = d.data();
        if (r.rating && typeof r.rating === 'number') {
          totalRating += r.rating;
          count++;
        }
      });
    }

    if (feedbackSnapshot && !feedbackSnapshot.empty) {
      feedbackSnapshot.forEach((d) => {
        const f = d.data();
        if (f.rating && typeof f.rating === 'number') {
          totalRating += f.rating;
          count++;
        }
      });
    }

    const averageRating = count > 0 ? totalRating / count : 0;
    await updateUserProfile(userId, { averageRating });
    return averageRating;
  } catch (error) {
    console.error('Error calculating average rating:', error);
    throw error;
  }
};
